//we need to create a theme context to store the theme mode and provide it to the app

import { createContext, useState, useMemo, useEffect } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

export const ThemeContext = createContext();

// Define color themes for light and dark modes
const getThemeTokens = (mode) => ({
  palette: {
    mode,
    ...(mode === 'light' ? {
      primary: {
        main: '#1976d2',
        light: '#42a5f5',
        dark: '#1565c0',
        contrastText: '#fff',
      },
      secondary: {
        main: '#9c27b0',
        light: '#ba68c8',
        dark: '#7b1fa2',
        contrastText: '#fff',
      },
      background: {
        default: '#fff',
        paper: '#f5f5f5',
      },
      text: {
        primary: 'rgba(0, 0, 0, 0.87)',
        secondary: 'rgba(0, 0, 0, 0.6)',
        disabled: 'rgba(0, 0, 0, 0.38)',
      },
      divider: 'rgba(0, 0, 0, 0.12)',
    } : {
      // Dark mode - monochromatic scheme
      primary: {
        main: '#e0e0e0',
        light: '#f5f5f5',
        dark: '#bdbdbd',
        contrastText: '#000',
      },
      secondary: {
        main: '#bdbdbd',
        light: '#e0e0e0',
        dark: '#9e9e9e',
        contrastText: '#000',
      },
      background: {
        default: '#121212',
        paper: '#1e1e1e',
      },
      text: {
        primary: '#ffffff',
        secondary: 'rgba(255, 255, 255, 0.7)',
        disabled: 'rgba(255, 255, 255, 0.5)',
      },
      divider: 'rgba(255, 255, 255, 0.12)',
      action: {
        active: 'rgba(255, 255, 255, 0.8)',
        hover: 'rgba(255, 255, 255, 0.08)',
        selected: 'rgba(255, 255, 255, 0.16)',
        disabled: 'rgba(255, 255, 255, 0.3)',
        disabledBackground: 'rgba(255, 255, 255, 0.12)',
      }
    })
  }
});

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    // Try to get the theme from localStorage, default to 'light' if not found
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'light';
  });

  // Save theme changes to localStorage
  useEffect(() => {
    localStorage.setItem('theme', mode);
  }, [mode]);

  // Generate the theme object
  const theme = useMemo(() => createTheme(getThemeTokens(mode)), [mode]);

  const value = useMemo(
    () => ({
      mode,
      setMode,
      toggleColorMode: () => setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light')),
    }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}
